export const NO_COURSES_SELECTED = 'No specific courses selected';

export const APPROVE_SUCCESS = 'Learning Hub access approved. The learner can enroll in all courses on this plan.';

export const DECLINE_SUCCESS = 'Learning Hub access request declined';

export const APPROVE_COPY = 'This uses one Learning Hub seat and unlocks every Learning Hub course for that learner, not only the selected courses. Selected courses are the learner\'s interest list only.';

export const DECLINE_COPY = 'Decline does not free or use a seat when the request is still pending.';

export const PLAN_NOT_ENABLED = 'Paid Learning Hub access is not enabled for this project';

export const NO_SEATS_REMAINING = 'No Learning Hub seats remaining on this project';

export const pendingRequestCount = (rows) => (
  (Array.isArray(rows) ? rows : []).filter((row) => row && row.status === 'pending').length
);

export const selectedCourseSummary = (row) => {
  const courses = Array.isArray(row?.selected_courses) ? row.selected_courses : [];
  const names = courses
    .map((course) => (typeof course?.name === 'string' ? course.name.trim() : ''))
    .filter(Boolean);
  if (!names.length) return NO_COURSES_SELECTED;
  if (names.length === 1) return names[0];
  return `${names[0]} + ${names.length - 1} more`;
};

export const projectNameFor = (projectId, projects) => {
  const match = (Array.isArray(projects) ? projects : []).find((project) => (
    project && (
      project.project_uuid === projectId
      || project.uuid === projectId
      || project.id === projectId
    )
  ));
  return match?.project_name || match?.name || projectId || '';
};

export const projectsFromResponse = (response) => {
  const rows = response?.results || response?.data;
  return Array.isArray(rows) ? rows : [];
};

export const requestsFromResponse = (response) => {
  if (!response || typeof response !== 'object') {
    return { rows: [], error: 'Unable to load Learning Hub upgrade requests' };
  }
  if ((response.httpStatus && response.httpStatus !== 200) || response.status === false) {
    return {
      rows: [],
      error: response.message || 'Unable to load Learning Hub upgrade requests',
    };
  }
  return {
    rows: Array.isArray(response.data) ? response.data : [],
    error: '',
  };
};

const accessRecord = (value) => (
  value && typeof value === 'object' && !Array.isArray(value)
  && (value.plan || value.seat_summary || value.seat_message || value.allocations)
    ? value
    : null
);

export const learningAccessFromResponse = (response) => {
  if (!response || typeof response !== 'object') return null;
  if (response.httpStatus && response.httpStatus !== 200) return null;
  const candidate = accessRecord(response.data?.learning_access)
    || accessRecord(response.learning_access)
    || accessRecord(response.data)
    || accessRecord(response);
  if (response.status === false && !candidate) return null;
  return candidate;
};

export const seatsAvailable = (access) => {
  const summary = Number(access?.seat_summary?.available);
  if (Number.isFinite(summary)) return summary;
  const allocations = Array.isArray(access?.allocations) ? access.allocations : [];
  return allocations.reduce((total, allocation) => {
    if (allocation?.status && allocation.status !== 'active') return total;
    const remaining = Number(allocation?.seats_remaining);
    return total + (Number.isFinite(remaining) ? remaining : 0);
  }, 0);
};

export const canApproveLearningHubRequest = (access) => (
  access?.plan === 'learning_hub' && seatsAvailable(access) > 0
);

export const approveBlockedMessage = (access) => {
  if (!access) return 'Seat availability could not be loaded.';
  if (access.plan !== 'learning_hub') return PLAN_NOT_ENABLED;
  if (seatsAvailable(access) <= 0) return NO_SEATS_REMAINING;
  return '';
};

export const filterRequests = (rows, { status = 'all', search = '', projects = [] } = {}) => {
  const query = String(search || '').trim().toLowerCase();
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    if (!row) return false;
    if (status && status !== 'all' && row.status !== status) return false;
    if (!query) return true;
    const courses = (Array.isArray(row.selected_courses) ? row.selected_courses : [])
      .flatMap((course) => [course?.name, course?.category, course?.provider])
      .filter(Boolean)
      .join(' ');
    const haystack = [
      row.user_email,
      projectNameFor(row.project_id, projects),
      row.comment,
      row.status,
      courses,
      selectedCourseSummary(row),
    ].join(' ').toLowerCase();
    return haystack.includes(query);
  });
};

export const reviewPayload = (status, comment) => {
  const payload = { status };
  const note = typeof comment === 'string' ? comment.trim() : '';
  if (note) payload.comment = note;
  return payload;
};

export const isReviewSuccess = (response) => (
  !!response && response.httpStatus === 200 && response.status !== false
);
